import { concat, isEmpty, get, cloneDeep, filter } from 'lodash';
import ProductCostingType from './productCosting.types';
import { Page_Size } from '../../../../Components/helper';
import type { BomComponent, CostingRecord, ListProductCostingPayload, ProductCostingAction, ProductCostingSliceState } from './productCosting.types';

/* initialState is a byte-for-byte copy of the JavaScript original
   (NewProductCosting/store/NewProductCostingReducer.reducer.js:5-39). The slice is still mounted
   under the key `newProductCostingData`, so the shape must not drift. */
const initialState: ProductCostingSliceState = {
  bomCompoList: [],
  productCosting: [],
  productCostingDetail: null,
  productCostingPreviousData: null,
  completionData: null,
  productCostingTotalCount: 0,
  allSelectedIds: [],
  costingCommentList: [],
  editCommentData: null,
  productColours: [],
  productLengths: [],
  productSizes: [],
  filterValues: {
    supplier: [],
    colourway: [],
    currency: [],
    incoterms: [],
    searchText: '',
    pageIndex: 1,
    pageSize: Page_Size,
    sortOrder: '',
    sortPropertyName: '',
    loading: false,
    sampleRef: [],
  },
  productUnitList: [],
  clearedHighlights: false,
  isRefreshed: false,
  productExchnageRate: null,
  costingTemplateDetail: null,
  costingStageListForNonIntegratedSupp: [],
  supplierFactories: [],
  averageTotalCal: null,
};

/* The five BOM type names the testing/detailed-costing grids expect first, in this order.
   Anything not named here is appended after, in whatever order the API returned it. */
const bomSequence = ['Materials', 'Artwork Applications', 'Trims', 'Labels', 'Packaging'];

/**
 * Decides whether an incoming page replaces the current list or is appended to it.
 * A non-empty searchText combined with isScrolled appends; searchText alone replaces;
 * isScrolled alone appends; otherwise the payload replaces wholesale.
 */
const setArrayByCase = (array: CostingRecord[], payload: ListProductCostingPayload): CostingRecord[] => {
  if (!isEmpty(get(payload, 'searchText')) && get(payload, 'isScrolled')) {
    return [...array, ...payload.data];
  }
  if (!isEmpty(get(payload, 'searchText'))) {
    return payload.data;
  }
  if (get(payload, 'isScrolled')) {
    return concat(array, payload.data);
  }
  return payload.data;
};

/**
 * Drops repeated entries while preserving first-seen order. Keeps the original's optional
 * chaining so a nullish payload still yields a nullish list rather than throwing.
 */
const removeDuplicates = (arr: string[]): string[] => arr?.filter((item, index) => arr?.indexOf(item) === index);

/**
 * Re-orders BOM components so the five known types lead in `bomSequence` order, with any
 * unrecognised type appended afterwards.
 */
const applyBomSequence = (payload: BomComponent[]): BomComponent[] => {
  let bomUpdatedSequence: BomComponent[] = [];
  bomSequence?.forEach((bom) => {
    const filterSeq = filter(payload, (o: BomComponent) => o?.bomType?.name === bom);
    if (filterSeq.length > 0) bomUpdatedSequence = concat(bomUpdatedSequence, filterSeq);
  });
  const filterNonSeq = filter(payload, (o: BomComponent) => !bomSequence?.includes(o?.bomType?.name as string));
  if (filterNonSeq.length > 0) bomUpdatedSequence = concat(bomUpdatedSequence, filterNonSeq);
  return bomUpdatedSequence;
};

/**
 * Product Costing slice, mounted in reducers/index.js under the key `newProductCostingData`.
 * Two of the declared action types (COSTING_ADD_PRODUCT_COSTING_DATA, COSTING_UPDATE_COSTING_COMMENT)
 * are fire-and-forget and deliberately have no case, matching the original.
 */
const productCostingReducer = (state: ProductCostingSliceState = initialState, action: ProductCostingAction): ProductCostingSliceState => {
  switch (action.type) {
    case ProductCostingType.COSTING_GET_TESTING_BOM_LIST:
      return {
        ...state,
        bomCompoList: applyBomSequence(action.payload),
      };
    case ProductCostingType.COSTING_LIST_PRODUCT_COSTING: {
      const updatedArry = cloneDeep(setArrayByCase(state.productCosting, action.payload));
      return {
        ...state,
        productCosting: updatedArry,
        productCostingTotalCount: action.payload.totalCount,
        averageTotalCal: action.payload.averageTotalCal,
      };
    }
    case ProductCostingType.COSTING_MAEK_AS_COMPLETE:
      return {
        ...state,
        completionData: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_COSTING_DETAIL_BY_ID:
    case ProductCostingType.COSTING_SET_UNINTEGRATED_PRODUCT_COSTING_DETAILS:
      return {
        ...state,
        productCostingDetail: action.payload,
      };
    case ProductCostingType.COSTING_REMOVE_PRODUCT_COSTING: {
      const filteredData = state.productCosting.filter((item) => action.payload.indexOf(item._id as string) === -1);
      return {
        ...state,
        productCosting: filteredData,
      };
    }
    case ProductCostingType.COSTING_DUPLICATE_PRODUCT_COSTING_DETAIL:
      return {
        ...state,
        productCostingPreviousData: action.payload,
      };
    case ProductCostingType.COSTING_SELECT_ALL_PRODUCT:
      return {
        ...state,
        allSelectedIds: action.payload,
      };
    case ProductCostingType.COSTING_LIST_PRODUCT_COSTING_COMMENT:
      return {
        ...state,
        costingCommentList: action.payload,
      };
    case ProductCostingType.COSTING_SET_DATA_TO_UPDATE_COMMENT:
      return {
        ...state,
        editCommentData: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_COLOUR:
    case ProductCostingType.COSTING_SET_UNINTEGRATED_COLOURWAYS:
      return {
        ...state,
        productColours: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_LENGTHS:
      return {
        ...state,
        productLengths: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_SIZES:
      return {
        ...state,
        productSizes: removeDuplicates(action.payload),
      };
    case ProductCostingType.COSTING_SET_FILTER_SORT_DATA:
      return {
        ...state,
        filterValues: { ...state.filterValues, ...action.payload },
      };
    case ProductCostingType.COSTING_SET_UNINTEGRATED_UNITS:
      return {
        ...state,
        productUnitList: action.payload,
      };
    case ProductCostingType.COSTING_SET_CLEARED_HIGHLIGHTS_STATUS:
      return {
        ...state,
        clearedHighlights: action.payload,
      };
    case ProductCostingType.COSTING_SET_REFRESHED_STATE:
      return {
        ...state,
        isRefreshed: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_COSTING_EXCHANGE_RATE:
      return {
        ...state,
        productExchnageRate: action?.payload,
      };
    case ProductCostingType.COSTING_PRODUCT_COSTING_UNMOUNT:
      return {
        ...state,
        ...initialState,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_COSTING_TEMPLATE_DETAIL_BY_ID:
      return {
        ...state,
        costingTemplateDetail: action.payload,
      };
    /* Retired by BM-9901 and no longer dispatched by this page, but deliberately kept: the
       out-of-scope JavaScript page still writes and reads this same slice, and
       tests/ProductCosting/reducerParity.test.ts asserts both reducers stay interchangeable while
       it is mounted. Delete both halves together when NewProductCosting goes. */
    case ProductCostingType.COSTING_SET_UNINTEGRATED_COSTINGSTAGE:
      return {
        ...state,
        costingStageListForNonIntegratedSupp: action.payload,
      };
    case ProductCostingType.COSTING_SET_PRODUCT_SUPPLERS_FACTORIES:
      return {
        ...state,
        supplierFactories: action.payload,
      };
    case ProductCostingType.COSTING_SET_COSTING_STATS:
      return {
        ...state,
        averageTotalCal: action.payload,
      };
    case ProductCostingType.COSTING_SET_UNMOUNT_STATE:
      return {
        ...state,
        productCostingDetail: null,
      };
    default:
      return state;
  }
};

export default productCostingReducer;
